import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useHeroEntrance from '../hooks/useHeroEntrance';
import { prefersReducedMotion } from '../hooks/useReducedMotion';
import {
  FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaFacebookF, FaTwitter,
  FaLinkedinIn, FaInstagram, FaWhatsapp, FaCheckCircle,
} from 'react-icons/fa';
import { HiArrowRight, HiSparkles } from 'react-icons/hi';

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', subject: '', message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [activeSection, setActiveSection] = useState('contact');
  const heroBadgeRef = useRef(null);
  const heroH1Ref = useRef(null);
  const heroParaRef = useRef(null);
  useHeroEntrance([
    heroBadgeRef,
    { ref: heroH1Ref, heading: true },
    heroParaRef,
  ]);

  /* Form: staggered field entrance on viewport enter */
  const formRef = useRef(null);
  useEffect(() => {
    if (!formRef.current || formSuccess) return;
    const ctx = gsap.context(() => {
      const fields = gsap.utils.toArray('.form-field');
      if (!fields.length) return;
      if (prefersReducedMotion()) {
        gsap.set(fields, { opacity: 1, y: 0 });
        return;
      }
      gsap.set(fields, { opacity: 0, y: 18, willChange: 'transform' });
      ScrollTrigger.create({
        trigger: formRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.to(fields, {
            opacity: 1, y: 0,
            duration: 0.7, stagger: 0.08, ease: 'power3.out',
          });
        },
      });
    }, formRef);
    return () => ctx.revert();
  }, [formSuccess]);

  /* Success: particle burst from check icon */
  const successRef = useRef(null);
  useEffect(() => {
    if (!formSuccess || !successRef.current) return;
    if (prefersReducedMotion()) return;
    const host = successRef.current;
    const colors = ['rgba(9,132,227,1)', 'rgba(0,176,255,1)', 'rgba(168,0,255,1)', 'rgba(34,197,94,1)'];
    const total = 28;
    for (let i = 0; i < total; i++) {
      const p = document.createElement('span');
      const size = gsap.utils.random(5, 10);
      p.style.cssText = `
        position:absolute; top:50%; left:50%;
        width:${size}px; height:${size}px;
        border-radius:9999px;
        background:${colors[i % colors.length]};
        pointer-events:none; will-change:transform,opacity;
        margin-top:-${size/2}px; margin-left:-${size/2}px;
        box-shadow: 0 0 12px ${colors[i % colors.length]};
      `;
      host.appendChild(p);
      const angle = (i / total) * Math.PI * 2 + gsap.utils.random(-0.15, 0.15);
      const dist  = gsap.utils.random(140, 240);
      gsap.fromTo(p,
        { x: 0, y: 0, scale: 0, opacity: 1 },
        {
          x: Math.cos(angle) * dist,
          y: Math.sin(angle) * dist,
          scale: gsap.utils.random(0.6, 1.2),
          opacity: 0,
          duration: gsap.utils.random(0.9, 1.4),
          ease: 'power3.out',
          onComplete: () => p.remove(),
        }
      );
    }
  }, [formSuccess]);

  const contactInfo = [
    { icon: <FaMapMarkerAlt />, title: 'Our Location', details: '1095, silver business point, VIP circle, utran, Surat 394105', color: 'from-nexcess-blue to-nexcess-cyan' },
    { icon: <FaPhoneAlt />, title: 'Call Us', details: '+91 79840 69470', color: 'from-nexcess-cyan to-nexcess-purple' },
    { icon: <FaEnvelope />, title: 'Email Us', details: 'Info@nexcessinfotech.com', color: 'from-nexcess-purple to-nexcess-blue' },
  ];

  const socialMedia = [
    { icon: <FaFacebookF />, link: 'https://facebook.com', label: 'Facebook' },
    { icon: <FaTwitter />, link: 'https://twitter.com', label: 'Twitter' },
    { icon: <FaLinkedinIn />, link: 'https://linkedin.com', label: 'LinkedIn' },
    { icon: <FaInstagram />, link: 'https://instagram.com', label: 'Instagram' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) {
        throw new Error('Please fill in all fields');
      }
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send message');
      setFormSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setFormSuccess(false), 3500);
    } catch (error) {
      alert(error.message || 'Failed to send message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: 'contact', label: 'Send Message' },
    { id: 'visit', label: 'Visit Us' },
  ];

  return (
    <>
      {/* HERO */}
      <section className="relative pt-36 pb-16 overflow-hidden bg-paper">
        <div className="absolute inset-0 bg-mesh-soft pointer-events-none" />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-nexcess-cyan/10 blur-3xl animate-blob pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full bg-nexcess-purple/10 blur-3xl animate-blob pointer-events-none" style={{ animationDelay: '4s' }} />

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div ref={heroBadgeRef} className="badge mb-5 mx-auto">
              <HiSparkles /> Begin a Project
            </div>
            <h1 ref={heroH1Ref} className="display-hero mb-5">
              Get in <span className="gradient-text">Touch</span>
            </h1>
            <p ref={heroParaRef} className="narrative-lg">
              Have questions or ready to start your next project? Reach out to us for a consultation.
            </p>
          </div>

          {/* Contact info cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            {contactInfo.map((info, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-hairline shadow-soft hover:shadow-soft-lg hover:-translate-y-1 transition-all duration-300 flex items-center gap-4"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${info.color} flex items-center justify-center text-white shrink-0 shadow-soft`}>
                  {info.icon}
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-ink-soft font-semibold mb-1">
                    {info.title}
                  </div>
                  <div className="text-ink font-medium text-sm leading-snug break-words">
                    {info.details}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tab pills */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-1 p-1 bg-white rounded-full border border-hairline shadow-soft">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveSection(t.id)}
                  className={`px-6 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 ${
                    activeSection === t.id
                      ? 'text-white shadow-soft'
                      : 'text-ink-soft hover:text-nexcess-blue'
                  }`}
                  style={
                    activeSection === t.id
                      ? { background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 60%, #d500f9 100%)' }
                      : {}
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="pb-20 md:pb-28 bg-paper">
        <div className="container-custom">
          {activeSection === 'contact' && (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Form */}
              <div className="lg:col-span-2 bg-white rounded-3xl p-8 md:p-10 border border-hairline shadow-soft">
                <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Send Message</div>
                <h2 className="heading-lg mb-2">Let's start a conversation</h2>
                <p className="narrative-body mb-8">We'd love to hear from you. Fill out the form below and we'll get back to you within 24 hours.</p>

                {formSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="relative rounded-2xl border border-green-200 bg-green-50 p-10 text-center overflow-hidden"
                  >
                    <div ref={successRef} className="absolute inset-0 pointer-events-none" />
                    <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white mx-auto mb-5 shadow-soft">
                      <FaCheckCircle size={28} />
                    </div>
                    <h3 className="relative text-2xl font-bold text-ink mb-2">Message Sent!</h3>
                    <p className="relative narrative-body">Thank you for reaching out. We'll be in touch shortly.</p>
                  </motion.div>
                ) : (
                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="form-field">
                        <label className="block text-sm font-semibold text-ink mb-2" htmlFor="name">Full Name</label>
                        <input
                          type="text" id="name" name="name" required
                          value={formData.name} onChange={handleInputChange}
                          className="input-field"
                          placeholder="John Doe"
                        />
                      </div>
                      <div className="form-field">
                        <label className="block text-sm font-semibold text-ink mb-2" htmlFor="email">Email Address</label>
                        <input
                          type="email" id="email" name="email" required
                          value={formData.email} onChange={handleInputChange}
                          className="input-field"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="form-field">
                      <label className="block text-sm font-semibold text-ink mb-2" htmlFor="phone">Phone Number</label>
                      <input
                        type="tel" id="phone" name="phone" required
                        value={formData.phone} onChange={handleInputChange}
                        className="input-field"
                        placeholder="+91 1234567890"
                      />
                    </div>

                    <div className="form-field">
                      <label className="block text-sm font-semibold text-ink mb-2" htmlFor="subject">Subject</label>
                      <input
                        type="text" id="subject" name="subject" required
                        value={formData.subject} onChange={handleInputChange}
                        className="input-field"
                        placeholder="How can we help you?"
                      />
                    </div>

                    <div className="form-field">
                      <label className="block text-sm font-semibold text-ink mb-2" htmlFor="message">Message</label>
                      <textarea
                        id="message" name="message" rows="5" required
                        value={formData.message} onChange={handleInputChange}
                        className="input-field resize-none"
                        placeholder="Your message here..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-primary w-full md:w-auto form-field"
                      style={{ willChange: 'transform' }}
                    >
                      {isSubmitting ? 'Sending…' : 'Send Message'} <HiArrowRight />
                    </button>
                  </form>
                )}
              </div>

              {/* Info sidebar */}
              <aside className="space-y-6">
                <div className="rounded-3xl p-8 text-white shadow-soft-lg relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0984e3 0%, #00b0ff 50%, #d500f9 100%)' }}>
                  <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
                  <div className="relative z-10">
                    <h3 className="text-xl font-bold mb-3">Need Quick Help?</h3>
                    <p className="text-white/90 text-sm mb-6 leading-relaxed">
                      Chat with us directly on WhatsApp or give us a call — we'll respond fast.
                    </p>
                    <div className="space-y-3">
                      <a
                        href="https://wa.me/917984069470"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/15 backdrop-blur-sm border border-white/20 hover:bg-white/25 transition-colors"
                      >
                        <FaWhatsapp size={18} />
                        <span className="font-semibold text-sm">Chat on WhatsApp</span>
                      </a>
                      <a
                        href="tel:+917984069470"
                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white text-nexcess-blue font-semibold text-sm hover:-translate-y-0.5 transition-transform"
                      >
                        <FaPhoneAlt size={14} />
                        <span>+91 79840 69470</span>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-7 border border-hairline shadow-soft">
                  <div className="text-sm font-semibold text-ink mb-4">Follow Us</div>
                  <div className="flex flex-wrap gap-2.5">
                    {socialMedia.map((s, i) => (
                      <a
                        key={i}
                        href={s.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 rounded-xl bg-paper-2 border border-hairline flex items-center justify-center text-ink-soft hover:text-white hover:border-transparent hover:-translate-y-0.5 transition-all"
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(135deg, #0984e3, #d500f9)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                        aria-label={s.label}
                      >
                        {s.icon}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-7 border border-hairline shadow-soft">
                  <div className="text-sm font-semibold text-ink mb-4">Office Hours</div>
                  <div className="space-y-2 text-sm">
                    {[
                      ['Mon – Fri', '9:00 AM – 6:00 PM'],
                      ['Saturday', '9:00 AM – 1:00 PM'],
                      ['Sunday', 'Closed'],
                    ].map(([d, t]) => (
                      <div key={d} className="flex justify-between py-1.5 border-b border-hairline last:border-0">
                        <span className="text-ink font-medium">{d}</span>
                        <span className="text-ink-soft">{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          )}

          {activeSection === 'visit' && (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-3xl p-8 md:p-10 border border-hairline shadow-soft">
                <div data-reveal="up" data-reveal-delay="0.05" className="badge mb-4">Visit Us</div>
                <h2 className="heading-lg mb-3">Visit Our Office</h2>
                <p className="narrative-body mb-8">
                  We'd love to meet you in person. Our office is located in the heart of Surat,
                  easily accessible by public transportation.
                </p>

                <div className="bg-paper-2 rounded-2xl p-6 mb-6">
                  <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-3">Address</div>
                  <p className="text-ink leading-relaxed mb-3 font-medium">
                    Nexcess Infotech<br />
                    1095, silver business point,<br />
                    VIP circle, utran,<br />
                    Surat 394105, Gujarat, India
                  </p>
                  <p className="text-ink-soft text-sm mb-5">
                    <span className="text-ink font-semibold">Landmark:</span> Near VIP Circle
                  </p>
                  <a
                    href="https://maps.app.goo.gl/caa7t1aQpKueVA1r6"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <FaMapMarkerAlt /> Get Directions
                  </a>
                </div>

                <div className="bg-paper-2 rounded-2xl p-6">
                  <div className="text-xs uppercase tracking-wider text-nexcess-blue font-semibold mb-3">Office Hours</div>
                  <div className="space-y-2 text-sm">
                    {[
                      ['Monday – Friday', '9:00 AM – 6:00 PM'],
                      ['Saturday', '9:00 AM – 1:00 PM'],
                      ['Sunday', 'Closed'],
                    ].map(([d, t]) => (
                      <div key={d} className="flex justify-between py-2 border-b border-hairline last:border-0">
                        <span className="text-ink font-medium">{d}</span>
                        <span className="text-ink-soft">{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden border border-hairline shadow-soft min-h-[500px]">
                <iframe
                  title="Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3719.0551927054276!2d72.8610172!3d21.2335127!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04f0037f7c2f5%3A0xb6a92bd1a49c1c21!2s1095%20Silver%20Business%20Point%2C%20Surat!5e0!3m2!1sen!2sin!4v1719156642042!5m2!1sen!2sin"
                  className="w-full h-full border-0"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Contact;
